import { apiError } from '../apiError';
import api from '../axios.config';
import { ApiResponse } from 'types';

/**
 * Operator kill switches — disable a channel, or everything, for an organization.
 *
 * MASTER_ADMIN only, and deliberately narrower than the usual admin gate:
 * SUPER_ADMIN is granted per service organization, so a reseller's own super
 * admin would otherwise be able to lift the suspension placed on that reseller.
 */

/**
 * The gating vocabulary. A tree, not a list — a lock on a parent covers its
 * children, so suspending WHATSAPP still stops WHATSAPP_CAMPAIGNS, while
 * locking WHATSAPP_CAMPAIGNS alone leaves ordinary conversations working.
 * Mirrors LockedFeature.java; the covering logic lives on the server.
 */
export type LockedFeature =
  | 'ALL'
  | 'WHATSAPP' | 'WHATSAPP_MESSAGING' | 'WHATSAPP_CAMPAIGNS'
  | 'VOICE' | 'SOFTPHONE' | 'OUTBOUND_CALLING' | 'VOICE_AI_CAMPAIGNS' | 'VOICE_PROVIDERS'
  | 'AI_AGENTS'
  | 'CRM' | 'CRM_INTEGRATIONS' | 'BOOKING'
  | 'SMS' | 'RCS' | 'EMAIL';

/** One feature's answer for the calling organisation. */
export interface FeatureAccess {
  /** May they act — send, start a campaign, write config. */
  allowed: boolean;
  /** May they still read their history. False only under BLOCK_ALL. */
  readable: boolean;
  parent: LockedFeature | null;
  mode?: LockMode;
  /** The operator's own words, so a billing hold does not read as an outage. */
  reason?: string;
}

export interface EffectiveFeatures {
  organizationId: string;
  features: Partial<Record<LockedFeature, FeatureAccess>>;
}

/**
 * ORG_AND_DESCENDANTS on a parent covers every organization beneath it,
 * including clients onboarded after the lock was set — so a reseller-wide
 * suspension is one row, not one per client.
 */
export type LockScope = 'ORG_ONLY' | 'ORG_AND_DESCENDANTS';

/** BLOCK_SEND stops outbound but leaves history/reports readable. */
export type LockMode = 'BLOCK_SEND' | 'BLOCK_ALL';

export type LockedAction = 'SEND' | 'READ' | 'INBOUND';

export interface FeatureLock {
  id?: string;
  organizationId: string;
  feature: LockedFeature;
  scope: LockScope;
  mode: LockMode;
  /**
   * Dropping inbound is irreversible — messages a customer sends during the
   * suspension cannot be re-fetched from Meta afterwards. Off unless asked for.
   */
  blockInbound: boolean;
  reason?: string;
  active: boolean;
  createdBy?: string;
  liftedBy?: string;
  liftedAt?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface LockCheckResult {
  organizationId: string;
  feature: LockedFeature;
  action: LockedAction;
  blocked: boolean;
  lockId?: string;
  /** For a cascaded suspension this is the parent, not the org you asked about. */
  lockedOnOrganizationId?: string;
  scope?: LockScope;
  mode?: LockMode;
  reason?: string;
}

const BASE = '/v1/feature-locks';
// Shared so a 403 reads as a refusal rather than a load failure.
const err = (e: any, msg: string) => apiError(e, msg);

export const featureLocksService = {
  list: async (organizationId?: string): Promise<ApiResponse<FeatureLock[]>> => {
    try {
      const q = organizationId ? `?organizationId=${encodeURIComponent(organizationId)}` : '';
      return (await api.get(`${BASE}${q}`)).data;
    } catch (e: any) { return err(e, 'Failed to load feature locks'); }
  },

  /**
   * What the CALLING organisation may use. Unlike everything else here this is
   * not MASTER_ADMIN-only: it answers for whoever asks, and it is what the nav
   * and the pages gate themselves on, so the UI and the API refuse from one
   * computation instead of two that drift.
   */
  effective: async (): Promise<ApiResponse<EffectiveFeatures>> => {
    try { return (await api.get(`${BASE}/effective`)).data; }
    catch (e: any) { return err(e, 'Failed to load feature access'); }
  },

  create: async (body: Partial<FeatureLock>): Promise<ApiResponse<FeatureLock>> => {
    try { return (await api.post(BASE, body)).data; } catch (e: any) { return err(e, 'Failed to set the lock'); }
  },

  /** Clears `active`; the row stays so the suspension history survives. */
  lift: async (id: string): Promise<ApiResponse<FeatureLock>> => {
    try { return (await api.post(`${BASE}/${id}/lift`)).data; } catch (e: any) { return err(e, 'Failed to lift the lock'); }
  },

  /**
   * Resolves the cascade for one organization. Worth calling before relying on
   * a parent-scoped lock — it is the only way to confirm the switch actually
   * reaches the client you meant without waiting for a customer to complain.
   */
  check: async (organizationId: string, feature: LockedFeature, action: LockedAction = 'SEND'):
    Promise<ApiResponse<LockCheckResult>> => {
    try {
      return (await api.get(
        `${BASE}/check?organizationId=${encodeURIComponent(organizationId)}&feature=${feature}&action=${action}`
      )).data;
    } catch (e: any) { return err(e, 'Check failed'); }
  },
};
